import type ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';

import {ReviewableQuery} from './__generated/ReviewableQuery';
import {getCycleScopedStatus, isPublishApprovedForScopedStatus} from './reviewBadgeShared';

type ReviewableGraphQL = ExcludeInaccessible<ReviewableQuery['reviewable']>;

export interface CyclicalReviewPublishState {
  loading: boolean;
  error: boolean;
  inaccessible: boolean;
  reviewRequired: boolean;
  isPublishBlockedByReview: boolean;
  isPublishOptionDisabled: boolean;
}

export interface DeriveCyclicalPublishStateInput {
  openVersionApiId?: string;
  isLatest: boolean;
  isPublishOptionDisabledFromParent?: boolean;
  queryState: 'pending' | 'error' | 'inaccessible' | 'ready';
  reviewable?: ReviewableGraphQL;
}

export function deriveCyclicalPublishState({
  openVersionApiId,
  isLatest,
  isPublishOptionDisabledFromParent = false,
  queryState,
  reviewable,
}: DeriveCyclicalPublishStateInput): CyclicalReviewPublishState {
  const parentPublishDisabled = Boolean(isPublishOptionDisabledFromParent);
  const hasResolvableVersionId = openVersionApiId != null;
  const withoutReviewData = {
    reviewRequired: false,
    isPublishBlockedByReview: false,
  } satisfies Pick<CyclicalReviewPublishState, 'reviewRequired' | 'isPublishBlockedByReview'>;

  if (queryState === 'pending') {
    return {
      ...withoutReviewData,
      loading: true,
      error: false,
      inaccessible: false,
      isPublishOptionDisabled: parentPublishDisabled || isLatest,
    };
  }

  if (queryState === 'error' || queryState === 'inaccessible') {
    // Without review data the client cannot tell whether a review process applies, so leave publish to
    // the server's approval gate: a blocked publish fails with "Must have an approved review before
    // publishing", which beats a tooltip-less disabled button on tenants that never use reviews.
    return {
      ...withoutReviewData,
      loading: false,
      error: queryState === 'error',
      inaccessible: queryState === 'inaccessible',
      isPublishOptionDisabled: parentPublishDisabled,
    };
  }

  const reviewRequired = (reviewable?.parentReviewLifecycles.length ?? 0) > 0;
  const reviewChanges = reviewable?.review?.reviewChanges ?? [];
  const scopedStatus =
    isLatest && hasResolvableVersionId ? getCycleScopedStatus(reviewChanges, openVersionApiId) : null;
  const isPublishBlockedByReview =
    reviewRequired &&
    isLatest &&
    (!hasResolvableVersionId || !isPublishApprovedForScopedStatus(scopedStatus));

  return {
    loading: false,
    error: false,
    inaccessible: false,
    reviewRequired,
    isPublishBlockedByReview,
    isPublishOptionDisabled: parentPublishDisabled || isPublishBlockedByReview || !isLatest,
  };
}
